import React, {useImperativeHandle, useRef} from 'react';
import {classes, style, vars} from './scroll-view.st.css';
import {
    assertNotNullable,
    Evaluable,
    evaluateWhenFunction,
    OmitStrict,
    Orientation,
    Values
} from '@wix/devzai-utils-common';
import {
    Scrollbar,
    useDomElementRefDelayed,
    useMaxScrollLeftBinding,
    useMaxScrollTopBinding, useRefsUnion,
    useScrollHeightBinding,
    useScrollValueBinding,
    useScrollWidthBinding
} from '../index';
import {domGetScrollbarSize, scrollIntoView} from '@wix/devzai-utils-dom';
import {useWindowDependantMemo} from '../hooks/use-window-dependant-memo/use-window-dependant-memo'

export enum ScrollViewScrollingDirection {
    Vertical = 'vertical',
    Horizontal = 'horizontal',
    All = 'all'
}

export const ScrollViewScrollbars = {
    Custom: 'Custom',
    NativeVisible: 'NativeVisible',
    NativeAuto: 'NativeAuto',
    Hidden: 'hidden'
} as const;

export type ScrollViewScrollbars = Values<typeof ScrollViewScrollbars>;

export namespace ScrollView {

    export type RenderProps = {
        scrollViewRef: React.RefObject<ScrollView>;
    }

    export interface Props extends React.HTMLAttributes<any> {
        scrollingDirection?: ScrollViewScrollingDirection;
        children: React.ReactNode;
        minThumbSize?: number;
        maxThumbSize?: number;
        scrolledContentWrapperClassName?: string;
        fixedContent?: Evaluable<(renderProps: RenderProps) => React.ReactNode>;
        scrollbars?: Evaluable<(scrollbarSpec: {nativeScrollbarSize: number}) => ScrollViewScrollbars>
    }

    export namespace scrollInnerElementIntoView {

        export type Options = OmitStrict<scrollIntoView.Options, 'boundary'>;
    }
}

export interface ScrollView {
    getScrolledContentElement () : HTMLElement;
    getScrollViewElement () : HTMLElement;
    scrollInnerElementIntoView (element: Element, options?: ScrollView.scrollInnerElementIntoView.Options) : void;
}

export const ScrollView = React.forwardRef<ScrollView, ScrollView.Props>((props, forwardedRef) => {

    const {
        scrollingDirection = ScrollViewScrollingDirection.All,
        children,
        minThumbSize = 50,
        maxThumbSize = Infinity,
        scrolledContentWrapperClassName,
        fixedContent,
        scrollbars = defaultScrollViewScrollbars,

        className,
        style: styleAttributes,
        ...htmlAttributes
    } = props;

    const supportVerticalScrolling =
        scrollingDirection === ScrollViewScrollingDirection.All || scrollingDirection === ScrollViewScrollingDirection.Vertical;
    const supportHorizontalScrolling =
        scrollingDirection === ScrollViewScrollingDirection.All || scrollingDirection === ScrollViewScrollingDirection.Horizontal;

    const scrollbarSize = useWindowDependantMemo(() => domGetScrollbarSize(), []) ?? 0;

    const scrollViewElementRef = useRef<HTMLDivElement>(null);
    const scrolledContentElementRef = useRef<HTMLDivElement>(null);

    const evaluatedScrollbars = evaluateWhenFunction(scrollbars, {
        nativeScrollbarSize: scrollbarSize
    })

    const shouldUseNativeScrollbars = evaluatedScrollbars !== ScrollViewScrollbars.Custom && evaluatedScrollbars !== ScrollViewScrollbars.Hidden;

    const internalRef = useRef<ScrollView>(null);
    const ref = useRefsUnion([internalRef, forwardedRef]);

    useImperativeHandle(ref, () => {
        return {
            getScrolledContentElement() {
                return assertNotNullable(scrolledContentElementRef.current);
            },
            getScrollViewElement() {
                return assertNotNullable(scrollViewElementRef.current);
            },
            scrollInnerElementIntoView (element: Element, options?: ScrollView.scrollInnerElementIntoView.Options) {
                scrollIntoView(element, {
                    behavior: 'instant',
                    scrollMode: 'always',
                    block: 'center',
                    boundary: assertNotNullable(scrolledContentElementRef.current),
                    ...options
                });
            }
        }
    });

    return (
        <div
            ref={scrollViewElementRef}
            {...htmlAttributes}
            className={style(
                classes.root,
                {
                    supportsHidingScrollbar: scrollbarSize === 0,
                    scrollingDirection: getScrollingDirectionCssState(scrollingDirection),
                    useNativeScrollbars: shouldUseNativeScrollbars,
                    useOverflowAuto: evaluatedScrollbars === ScrollViewScrollbars.NativeAuto
                },
                props.className
            )}
            style={{
                ...styleAttributes,
                [vars.scrollbarSize]: scrollbarSize + 'px'
            }}
        >
            <div
                ref={scrolledContentElementRef}
                className={style(
                    classes.scrolledContentWrapper,
                    scrolledContentWrapperClassName
                )}
            >
                {children}
            </div>
            {
                supportVerticalScrolling && !shouldUseNativeScrollbars && evaluatedScrollbars !== ScrollViewScrollbars.Hidden ?
                    <Scrollbar
                        minThumbSize={minThumbSize}
                        maxThumbSize={maxThumbSize}
                        className={style(classes.verticalScrollbar, {})}
                        orientation={Orientation.Vertical}
                        scrollViewRef={scrolledContentElementRef}
                        thumb={renderThumb}
                    /> :
                    null
            }
            {
                supportHorizontalScrolling && !shouldUseNativeScrollbars && evaluatedScrollbars !== ScrollViewScrollbars.Hidden ?
                    <Scrollbar
                        minThumbSize={minThumbSize}
                        maxThumbSize={maxThumbSize}
                        className={style(classes.horizontalScrollbar, {})}
                        orientation={Orientation.Horizontal}
                        scrollViewRef={scrolledContentElementRef}
                        thumb={renderThumb}
                    /> :
                    null
            }
            {evaluateWhenFunction(fixedContent, {
                scrollViewRef: internalRef
            })}
        </div>
    );
});

const defaultScrollViewScrollbars: ScrollView.Props['scrollbars'] = (scrollbarSpec) => {
    return scrollbarSpec.nativeScrollbarSize === 0 ?
        ScrollViewScrollbars.NativeAuto :
        ScrollViewScrollbars.Custom
}

function renderThumb (renderProps: {isActive: boolean}) {

    const {
        isActive
    } = renderProps;

    return (
        <div className={style(classes.scrollbarThumb, {isActive})}/>
    )
}

function getScrollingDirectionCssState (scrollingDirection: ScrollViewScrollingDirection) {
    switch (scrollingDirection) {
        case ScrollViewScrollingDirection.All: return 'all';
        case ScrollViewScrollingDirection.Vertical: return 'vertical';
        case ScrollViewScrollingDirection.Horizontal: return 'horizontal';
    }
}

export function useScrolledContentElementRef (scrollViewRef: React.RefObject<ScrollView>) {
    return useDomElementRefDelayed(() => {
        return assertNotNullable(scrollViewRef.current).getScrolledContentElement();
    }, [scrollViewRef]);
}

export function useScrollViewScrollTopBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useScrollValueBinding('vertical', scrolledContentElement)
}

export function useScrollViewScrollLeftBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useScrollValueBinding('horizontal', scrolledContentElement)
}

export function useScrollViewScrollHeightBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useScrollHeightBinding(scrolledContentElement)
}

export function useScrollViewScrollWidthBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useScrollWidthBinding(scrolledContentElement)
}

export function useScrollViewMaxScrollTopBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useMaxScrollTopBinding(scrolledContentElement)
}

export function useScrollViewMaxScrollLeftBinding (scrollViewRef: React.RefObject<ScrollView>) {
    const scrolledContentElement = useScrolledContentElementRef(scrollViewRef);

    return useMaxScrollLeftBinding(scrolledContentElement)
}